import { useAvailableShopsQuery } from '@@partner:graphql/query/catalog/availableShops.gql'
import { price, ProductStock, Shop } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'
import AppLoader from '@components/common/AppLoader'
import { ActionCell } from '@components/common/form/ActionCell'
import { EditableCell } from '@components/common/form/EditableCell'
import { mergeLeft } from 'rambda'
import React, { useCallback, useEffect, useState } from 'react'
import { Checkbox, Col, InputNumber, Row, Table } from 'rsuite'
import ProductShopsSelector from '../ProductShopSelector'


interface ProductStockState {
  useSingleStock: boolean
  singleStock: price
  stocks: (ProductStock & { status?: string | null})[]
}

type props = {
  shopIds: GQL_ID[]
  stocks: ProductStock[]
  loadingProduct: boolean
  onChange?: (prices: ProductStockState) => void
  onShopSelect?: (selectedShopIds: GQL_ID[]) => void
}

const { Column, HeaderCell, Cell } = Table

export default function ProductStocksTable ({
  shopIds,
  stocks,
  loadingProduct,
  onChange,
  onShopSelect
}: props) {
  const { data, loading } = useAvailableShopsQuery(
    {
      ids: shopIds
    },
    shopIds.length === 0
  )

  
  const [ state, setState ] = useState<ProductStockState>({
    stocks,
    singleStock: 0,
    useSingleStock: false
  })

  useEffect(() => {
    const newStocks = shopIds.map(shopId => {
      const oldStock = stocks.find(stock => stock.shop_id === shopId)
      if (oldStock) {
        return oldStock
      }
      else {
        return {
          shop_id: shopId,
          amount: 0,
          status: 'EDIT'
        }
      }
    })
    setState((state) => ({
      ...state,
      stocks: newStocks
    }))
  }, [ shopIds, stocks ])

  const updateState = useCallback(
    (changes: Partial<ProductStockState>) => {
      const newState = {
        ...state,
        stocks: changes.stocks ?? state.stocks,
        singleStock: changes.singleStock ?? state.singleStock
      }
      setState(newState)
      onChange?.(newState)
    },
    [ onChange, state ]
  )

  const handleUseSingleStockChange = useCallback(
    (_, useSingleStock: boolean) => {
      updateState({ useSingleStock })
    },
    [ updateState ]
  )

  const handleSingleStockChange = useCallback(
    (singleStock) => {
      updateState({ singleStock })
    },
    [ updateState ]
  )

  const handleShopStockChange = useCallback(
    (stockObj: ProductStock) => {
      const existingIndex = state.stocks.findIndex(stock => stock.shop_id === stockObj.shop_id)

      if (existingIndex > -1) {
        const newStocks = state.stocks.map(stock => {
          if (stock.shop_id === stockObj.shop_id) {
            return {
              ...stock,
              amount: stockObj.amount
            }
          }
          return stock
        })
        updateState({
          stocks: newStocks
        })
      }
      else {
        const newObj = {
          ...stockObj,
          status: 'EDIT'
        }
        updateState({
          stocks: [
            ...state.stocks,
            newObj
          ]
        })
      }
    },
    [ state.stocks, updateState ]
  )

  const handleEdit = (shop_id: GQL_ID) => {
    
    const newState = state.stocks.map(stock => {
      if (stock.shop_id === shop_id) {
        return {
          ...stock,
          status: stock.status ? null : 'EDIT'
        }
      }
      return stock
    })
    
    setState((state) => ({
      ...state,
      stocks: newState
    }))
  }

  return (
    <Row>
      
      <Row>
        <Table
          data={state.stocks}
          rowHeight={60}
          autoHeight
          loading={loading || loadingProduct}
          renderLoading={() =>
            loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
          }
          affixHeader
        >
          <Column verticalAlign="middle">
            <HeaderCell>Магазин</HeaderCell>
            <Cell dataKey="shop_id" />
          </Column>
          <Column flexGrow={1} verticalAlign="middle">
            <HeaderCell>Адрес</HeaderCell>
            <Cell dataKey="address">
              {(stock) => (
                <div>{ data?.shops.find(shop => shop.id === stock.shop_id)?.address }</div>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} verticalAlign="middle">
            <HeaderCell>Остаток</HeaderCell>
            <EditableCell
              dataType='number'
              dataKey="amount"
              idKey='shop_id'
              rowData={state.stocks}
              onChange={(shop_id: GQL_ID, data: string, amount: number) => {
                handleShopStockChange({
                  shop_id,
                  amount
                })
              }}
            />
          </Column>
          <Column flexGrow={1} verticalAlign="middle">
            <HeaderCell>Изменить</HeaderCell>
            <ActionCell
              dataKey={'shop_id'}
              rowData={state.stocks}
              onEdit={handleEdit}
            />
          </Column>
        </Table>
      </Row>
    </Row>
    
  )
}